<template>
	<div id="app">
		<neko-default-layout>
			<div v-if="!login.authenticated" class="is-flex is-flex-direction-column is-align-items-center">
				<h1
					v-show="!login.authenticating"
					class="title is-1 has-text-centered has-text-grey-light mt-6"
					v-t="'title.not.authenticated'"
				></h1>
				<h1
					v-show="login.authenticating"
					class="title is-1 has-text-centered has-text-grey-light mt-6"
					v-t="'title.authenticating'"
				></h1>

				<router-link
					to="/login"
					class="button is-primary is-large mt-6"
					:disabled="login.authenticating"
					:class="{ 'is-loading': login.authenticating }"
					v-t="'action.login'"
				/>
			</div>

			<div v-else-if="!login.cabinet" class="columns">
				<div class="column is-one-third is-offset-4">
					<article class="message is-warning">
						<div class="message-body" v-t="'no.cabinet.selected'"></div>
					</article>

					<div class="box p-0">
						<neko-cabinet-selector v-model="cabinetSelected" />
					</div>
				</div>
			</div>
			<div v-else-if="loading" class="content has-text-centered has-text-grey mt-6">
				<p class="is-size-1">Chargement de l'application ...</p>
			</div>
			<transition v-else name="fade-fast" mode="out-in">
				<router-view v-if="reloadRouterView" />
			</transition>
		</neko-default-layout>
	</div>
</template>

<style lang="sass">
@import "@/styles/main.sass"
</style>

<i18n>
{
	"fr": {
		"action.login": "Se connecter",
		"title.not.authenticated": "Veuillez-vous authentifier pour accèder à l'application.",
		"no.cabinet.selected": "Vous devez être affilié à un cabinet pour continuer à utiliser cette application. Si vous n'avez pas la possibilité de choisir, veuillez contacter le support.",
		"title.authenticating": "Authentification en cours veuillez patienter."
	}
}
</i18n>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import { Watch } from "vue-property-decorator";
import { State } from "vuex-class";

import { NoCache } from "@/decorators/NoCache";

import NekoDefaultLayout from "@/layouts/Default.vue";

import { LoginState } from "@/lib/keycloak/typings/LoginState";

import { CabinetSelector as NekoCabinetSelector } from "@/components/cabinetSelector";
import { getAuditContextStored, isAuditContext } from "@/components/contextSelector";
import { ContextState } from "@/components/contextSelector/typings";

import { Cabinet } from "./lib/keycloak/typings/EmaSoftsToken";

@Component({
	components: {
		NekoCabinetSelector,
		NekoDefaultLayout
	}
})
export default class AppVue extends Vue {
	@State("login")
	public login!: LoginState;

	@State("context")
	public contextState!: ContextState;

	public reloadRouterView: boolean = true;
	public loading: boolean = true;

	private async created() {
		this.$router.beforeEach((to, from, next) => {
			this.$Progress.start();
			next();
		});

		this.$router.afterEach((to, from) => {
			this.$Progress.finish();
		});

		this.$router.onError(() => {
			this.$Progress.fail();
		});

		try {
			if (!this.cabinetSelected) {
				this.$store.commit("context/deleteContext");
			} else if (this.authenticated) {
				const context = await getAuditContextStored();

				if (isAuditContext(context)) {
					this.$store.commit("context/chooseAudit", context.audit);
				}
			}
		} catch (error) {
			console.error(error);
			this.$store.commit("context/deleteContext");
		} finally {
			this.loading = false;
		}
	}

	private get currentUser() {
		return this.login.currentUser;
	}

	private get authenticated() {
		return this.login.authenticated;
	}

	@NoCache
	public get cabinetSelected() {
		return this.login.cabinet;
	}

	public set cabinetSelected(cabinet: Cabinet | null) {
		this.$store.commit("login/setCabinet", cabinet);
	}

	@Watch("login.cabinet")
	private async watchCabinet(value: Cabinet, oldValue: Cabinet) {
		if (value?.id === oldValue?.id) {
			return;
		}

		this.$store.commit("context/deleteContext");

		if (oldValue && this.$route.name !== "audits") {
			await this.$router.push({ name: "audits" });
		}
	}
}
</script>
